import { getImageThumbnailUrl } from '@/utils/getImageThumbnailUrl';
import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ReactImageMagnify from 'react-image-magnify';
import { DEFAULT_IMAGE_OBJECT, MEDIA_TYPES } from '../../../../utils/constants';

import {
  DesktopComponentContainer,
  DesktopComponentMainImageContainer,
  MainImageVideoIconContainer,
  SideImagePanelContainer,
  MagnifyImageContainer,
} from './DesktopImageComponent.styles';
import { PlayIcon } from '@/assets/svgExports/PlayIcon';
import { getSrcSetFromCompressedImages } from '../../../../utils/compressedImagesHelpers';
import { SIDE_PANEL_MAX_IMAGE_LIMIT } from './constants';
import {
  getImageDimensions,
  getThumbnailImageDimensions,
  getEnlargedImageContainerDimensions,
  getZoomedImageDimension,
} from './utils';
import { isThemeCustomizableForStore } from 'src/utils/isThemeCustomizableForStore';
import { SidePanelImages } from './SidePanelImages';
import { MediaContainer } from 'src/components/ProductMediaRenderer/MediaContainer';
import WishlistHeartButton from 'src/components/Wishlist/WishlistHeartButton';
import PdpShareOptions from '../../SubComponents/ShareOptions';
import { isWishlistUnlockedAndActive } from 'src/utils/isWishlistUnlockedAndActive';

function DesktopImageComponent(props) {
  const {
    theme: { product_aspect_ratio: aspectRatio },
  } = useSelector((state) => ({
    theme: state.storeReducer.store.theme,
  }));
  const isAspectRatioAllowed = isThemeCustomizableForStore();

  const imageDimensions = getImageDimensions(aspectRatio, isAspectRatioAllowed);
  const enlargedImageContainerDimensions = getEnlargedImageContainerDimensions(
    aspectRatio,
    isAspectRatioAllowed
  );
  const thumbnailImageDimensions = getThumbnailImageDimensions(
    aspectRatio,
    isAspectRatioAllowed
  );
  const zoomedImageDimensions = getZoomedImageDimension(
    aspectRatio,
    isAspectRatioAllowed
  );
  // const playIconPosition = getPlayIconPosition(aspectRatio, isAspectRatioAllowed);

  const {
    handleShowImageDescriptionModal,
    productData,
    activeIdx,
    handleToggleWishlistClick,
  } = props;
  const [sidePanelImages, setSidePanelImages] = useState([]);
  const [mainImage, setMainImage] = useState();

  const [selectedVariant, setSelectedVariant] = useState(
    productData?.variants?.[activeIdx] || {}
  );

  const imageRef = useRef();

  useEffect(() => {
    handleSidePanelImages();
  }, [productData, selectedVariant]);

  useEffect(() => {
    !!sidePanelImages?.length && setMainImage(sidePanelImages[0]);
  }, [sidePanelImages]);

  useEffect(() => {
    if (activeIdx !== -1) setSelectedVariant(productData?.variants?.[activeIdx]);
  }, [activeIdx]);

  useEffect(() => {
    imageRef?.current
      ?.getElementsByTagName('img')?.[0]
      ?.setAttribute('property', 'image');
  }, [productData?.name]);

  useEffect(() => {
    props?.setMainImageIndexFromPdp(
      sidePanelImages.findIndex((item) => item?.image_url === mainImage?.image_url)
    );
  }, [mainImage]);

  function handleSidePanelViewMore(e) {
    e?.stopPropagation();
    if (mainImage?.image_url === DEFAULT_IMAGE_OBJECT.image_url) return;
    handleShowImageDescriptionModal();
  }

  function setImagesifVariantNotPresent() {
    if (!productData?.images?.length) {
      setSidePanelImages([DEFAULT_IMAGE_OBJECT]);
    } else {
      setSidePanelImages(productData?.images);
    }
  }

  function setImagesifVariantPresent() {
    if (productData?.variants?.length) {
      let tempImages = !!selectedVariant?.images
        ? selectedVariant?.images.slice(0, SIDE_PANEL_MAX_IMAGE_LIMIT)
        : [];
      if (productData?.images && productData?.images?.length) {
        if (tempImages?.length) tempImages = tempImages.concat(productData?.images);
        else tempImages = productData?.images?.slice(0, productData?.images?.length);
      }
      if (!tempImages?.length) tempImages?.push(DEFAULT_IMAGE_OBJECT);
      setSidePanelImages(tempImages);
    } else {
      const tempImages = productData?.images?.slice(0, productData?.images?.length);
      setSidePanelImages(tempImages);
    }
  }

  function handleSidePanelImages() {
    if (!productData?.variants_count) {
      setImagesifVariantNotPresent();
    } else {
      setImagesifVariantPresent();
    }
  }

  function handleSidePanelImage(item) {
    if (
      item?.media_type === MEDIA_TYPES.YOUTUBE_VIDEO ||
      item?.media_type === MEDIA_TYPES.VIDEO
    ) {
      handleShowImageDescriptionModal();
    }
    setMainImage(item);
  }

  function handleMainImageView(item) {
    if (item?.media_type === MEDIA_TYPES.YOUTUBE_VIDEO) {
      return getImageThumbnailUrl(item);
    }
    return item?.image_url;
  }

  return (
    <DesktopComponentContainer
      isCustomWebsite={isAspectRatioAllowed}
      className="tw-mx-auto"
    >
      <SideImagePanelContainer>
        <SidePanelImages
          sidePanelImages={sidePanelImages}
          thumbnailImageDimensions={thumbnailImageDimensions}
          mainImage={mainImage}
          handleSidePanelViewMore={handleSidePanelViewMore}
          handleSidePanelImage={handleSidePanelImage}
        />
      </SideImagePanelContainer>
      <MediaContainer
        width={imageDimensions.width}
        containerClasses="tw-ml-[16px] !tw-overflow-visible"
      >
        <DesktopComponentMainImageContainer
          ref={imageRef}
          isCustomWebsite={isAspectRatioAllowed}
          imageDimensions={imageDimensions}
          mediaType={mainImage?.media_type}
          onClick={handleSidePanelViewMore}
        >
          <MagnifyImageContainer>
            {isWishlistUnlockedAndActive() && (
              <div
                className="tw-absolute tw-right-[20px] tw-top-[20px] tw-z-10 tw-cursor-pointer"
                onClick={(e) => handleToggleWishlistClick(e)}
              >
                <WishlistHeartButton isActive={props?.isItemWishlisted} scale={1.43} />
              </div>
            )}
            <div
              className={`tw-absolute tw-right-[20px] ${isWishlistUnlockedAndActive() ? 'tw-top-[60px]' : 'tw-top-[20px]'} tw-z-10 tw-flex tw-h-[32px] tw-w-[32px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[#E0E0E0] tw-bg-white`}
            >
              <PdpShareOptions productData={productData} />
            </div>
            {(mainImage?.image_url || mainImage?.thumbnail_url) &&
              (mainImage?.media_type !== MEDIA_TYPES.VIDEO ? (
                <ReactImageMagnify
                  {...{
                    className: '!tw-h-full !tw-w-full !tw-overflow',
                    smallImage: {
                      alt: mainImage?.alt_text || productData?.name,
                      src: handleMainImageView(mainImage),
                      srcSet: getSrcSetFromCompressedImages(
                        mainImage?.compressed_images,
                        handleMainImageView(mainImage)
                      ),
                      isFluidWidth: true,
                    },
                    largeImage: {
                      src: handleMainImageView(mainImage),
                      ...zoomedImageDimensions,
                    },
                    enlargedImageContainerDimensions,
                    enlargedImageContainerStyle: {
                      backgroundColor: '#ffffff',
                      zIndex: 10,
                      marginLeft: 42,
                      boxShadow: '0 0 24px rgba(0,0,0,0.34)',
                      borderRadius: 16,
                      display:
                        mainImage?.image_url === DEFAULT_IMAGE_OBJECT.image_url
                          ? 'none'
                          : '',
                    },
                    enlargedImageStyle: {
                      objectFit: 'cover',
                      maxWidth: 1000,
                      display:
                        mainImage?.image_url === DEFAULT_IMAGE_OBJECT.image_url
                          ? 'none'
                          : '',
                    },
                    lensStyle: {
                      display:
                        mainImage?.image_url === DEFAULT_IMAGE_OBJECT.image_url
                          ? 'none'
                          : '',
                    },
                    imageClassName: '!tw-object-contain !tw-w-full !tw-h-full',
                    shouldUsePositiveSpaceLens: true,
                  }}
                />
              ) : (
                <img
                  src={mainImage?.thumbnail_url}
                  className="tw-h-full tw-w-full tw-object-contain"
                />
              ))}
          </MagnifyImageContainer>
          {(mainImage?.media_type === MEDIA_TYPES.YOUTUBE_VIDEO ||
            mainImage?.media_type === MEDIA_TYPES.VIDEO) && (
            <MainImageVideoIconContainer>
              <PlayIcon height={64} width={64} />
            </MainImageVideoIconContainer>
          )}
        </DesktopComponentMainImageContainer>
      </MediaContainer>
    </DesktopComponentContainer>
  );
}

export default DesktopImageComponent;
